// Evidence gallery for a care program: image + short video uploads.
// Nurses with access to the program can upload + view; only admin can delete.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ImagePlus,
  Video,
  Upload,
  Trash2,
  Loader2,
  Camera,
  X,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ACCEPT = "image/jpeg,image/png,image/heic,image/heif,image/webp,video/mp4,video/quicktime,video/webm";
const MAX_IMAGE_MB = 12;
const MAX_VIDEO_MB = 60;
const RESIZE_MAX_WIDTH = 1024;  // resize bigger images down to this width (preserves aspect ratio)
const RESIZE_QUALITY = 0.85;
const HEIC_MIMES = new Set(["image/heic", "image/heif"]);

// Downscale an image File to RESIZE_MAX_WIDTH (keeping aspect ratio) and
// re-encode as JPEG. Returns a new File. Used only when the source image is
// larger than the threshold. Browsers cannot decode HEIC, so we leave those
// files untouched (the upload still goes through, just bigger).
async function resizeImageFile(file) {
  if (HEIC_MIMES.has((file.type || "").toLowerCase())) return file;
  if (!file.type.startsWith("image/")) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    if (img.naturalWidth <= RESIZE_MAX_WIDTH) return file;
    const scale = RESIZE_MAX_WIDTH / img.naturalWidth;
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", RESIZE_QUALITY));
    if (!blob) return file;
    const baseName = (file.name || "evidence").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch (e) {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function bytesLabel(n) {
  if (!n) return "0 KB";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch {
    return iso;
  }
}

// Hook: build a direct URL that the browser can hit with credentials. The
// access_token cookie is sent automatically for both <img src> and <video src>
// because the cookie is SameSite=None; Secure.
function evidenceFileUrl(evidenceId) {
  return `${BACKEND}/api/evidence/${evidenceId}/file`;
}

function EvidenceThumb({ ev, onOpen }) {
  const url = evidenceFileUrl(ev.id);
  return (
    <button
      type="button"
      onClick={() => onOpen(ev)}
      className="group relative flex aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-100 hover:border-[#1D4ED8] hover:shadow-md transition-all"
      data-testid={`evidence-thumb-${ev.id}`}
    >
      {ev.kind === "video" ? (
        <video
          src={url}
          className="h-full w-full object-cover"
          muted
          playsInline
          preload="metadata"
          crossOrigin="use-credentials"
        />
      ) : (
        <img
          src={url}
          alt={ev.description || ev.original_filename}
          className="h-full w-full object-cover"
          crossOrigin="use-credentials"
          loading="lazy"
        />
      )}
      {ev.kind === "video" && (
        <span className="absolute bottom-1 right-1 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white">
          <Video className="h-3 w-3" /> VIDEO
        </span>
      )}
      {ev.description && (
        <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-2 pb-1 pt-3 text-left text-[10px] font-semibold text-white">
          {ev.description}
        </span>
      )}
    </button>
  );
}

function ViewerModal({ ev, onClose }) {
  const url = evidenceFileUrl(ev.id);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      data-testid="evidence-viewer"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        data-testid="evidence-viewer-close"
      >
        <X className="h-5 w-5" />
      </button>
      <div
        className="flex max-h-full max-w-5xl flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {ev.kind === "video" ? (
          <video src={url} controls autoPlay crossOrigin="use-credentials" className="max-h-[80vh] max-w-full rounded-xl" />
        ) : (
          <img src={url} alt={ev.description || ev.original_filename} crossOrigin="use-credentials" className="max-h-[80vh] max-w-full rounded-xl" />
        )}
        <div className="text-center text-xs text-white/80">
          <p className="font-bold">{ev.description || ev.original_filename}</p>
          <p>
            {ev.uploaded_by_name || ev.uploaded_by_email} - {formatDate(ev.uploaded_at)} - {bytesLabel(ev.size)}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function EvidenceGallery({ programId, canDelete }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // {file, kind}
  const [description, setDescription] = useState("");
  const [viewer, setViewer] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!programId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/${programId}/evidence`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error cargando evidencias");
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  const handleFile = (f) => {
    if (!f) return;
    const mime = (f.type || "").toLowerCase();
    let kind, maxMb;
    if (mime.startsWith("image/")) { kind = "image"; maxMb = MAX_IMAGE_MB; }
    else if (mime.startsWith("video/")) { kind = "video"; maxMb = MAX_VIDEO_MB; }
    else { toast.error(`Tipo no permitido: ${mime || "desconocido"}`); return; }
    if (f.size > maxMb * 1024 * 1024) {
      toast.error(`Archivo demasiado grande (${(f.size / 1024 / 1024).toFixed(1)} MB). Maximo ${maxMb} MB para ${kind === "image" ? "imagenes" : "videos"}.`);
      return;
    }
    setPendingFile({ file: f, kind });
    setDescription("");
  };

  const cancelPending = () => { setPendingFile(null); setDescription(""); };

  const doUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      // Client-side downscale for big phone-camera photos so the upload + PDF
      // generation stay snappy on hospital wifi.
      let fileToSend = pendingFile.file;
      if (pendingFile.kind === "image") {
        const before = fileToSend.size;
        fileToSend = await resizeImageFile(fileToSend);
        if (fileToSend !== pendingFile.file && fileToSend.size < before) {
          const saved = ((before - fileToSend.size) / before) * 100;
          if (saved >= 20) {
            toast.success(`Imagen optimizada (${saved.toFixed(0)}% mas ligera)`);
          }
        }
      }
      const form = new FormData();
      form.append("file", fileToSend);
      form.append("description", description.trim());
      const { data } = await api.post(`/programs/${programId}/evidence`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((cur) => [data, ...cur]);
      toast.success("Evidencia subida");
      cancelPending();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/evidence/${deleteTarget.id}`);
      setItems((cur) => cur.filter((e) => e.id !== deleteTarget.id));
      toast.success("Evidencia eliminada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar");
    } finally {
      setDeleteTarget(null);
    }
  };

  const counts = useMemo(() => {
    const c = { image: 0, video: 0 };
    for (const it of items) c[it.kind] = (c[it.kind] || 0) + 1;
    return c;
  }, [items]);

  if (!programId) {
    return (
      <p className="text-sm text-slate-500">
        Guarda primero el programa para poder adjuntar evidencias.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="evidence-section">
      {/* Counts + upload buttons */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-blue-800" data-testid="evidence-count-image">
            {counts.image} imagen{counts.image === 1 ? "" : "es"}
          </span>
          <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-indigo-800" data-testid="evidence-count-video">
            {counts.video} video{counts.video === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="evidence-camera-input"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="evidence-file-input"
          />
          <Button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className="h-10 rounded-xl bg-[#1D4ED8] px-3 text-xs font-semibold hover:bg-[#1E40AF]"
            disabled={uploading}
            data-testid="evidence-camera-btn"
          >
            <Camera className="mr-1.5 h-4 w-4" /> Tomar foto
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="h-10 rounded-xl border-[#1D4ED8] px-3 text-xs font-semibold text-[#1D4ED8] hover:bg-blue-50"
            disabled={uploading}
            data-testid="evidence-upload-btn"
          >
            <Upload className="mr-1.5 h-4 w-4" /> Subir archivo
          </Button>
        </div>
      </div>

      {/* Pending file (description + confirm) */}
      {pendingFile && (
        <div
          className="rounded-xl border-2 border-[#1D4ED8] bg-blue-50 p-3"
          data-testid="evidence-pending-form"
        >
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-blue-900">
            {pendingFile.kind === "image" ? (
              <ImagePlus className="h-4 w-4" />
            ) : (
              <Video className="h-4 w-4" />
            )}
            Listo para subir: {pendingFile.file.name}
            <span className="ml-auto text-xs font-normal text-blue-700">
              {bytesLabel(pendingFile.file.size)}
            </span>
          </div>
          <Label className="text-xs font-semibold text-blue-900">
            Descripcion (opcional)
          </Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Ej. "Herida sacro dia 3", "Caida en sala"'
            maxLength={500}
            className="mt-1 h-10 text-base"
            data-testid="evidence-desc-input"
          />
          <div className="mt-2 flex gap-2">
            <Button
              onClick={doUpload}
              disabled={uploading}
              className="flex-1 rounded-xl bg-[#1D4ED8] font-semibold hover:bg-[#1E40AF]"
              data-testid="evidence-upload-confirm"
            >
              {uploading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Subiendo...</>
              ) : (
                <>Subir evidencia</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={cancelPending}
              disabled={uploading}
              className="rounded-xl"
              data-testid="evidence-upload-cancel"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Gallery grid */}
      {loading ? (
        <div className="flex h-20 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500"
          data-testid="evidence-empty"
        >
          Aun no hay evidencias en este turno. Toma una foto o sube un archivo.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5" data-testid="evidence-grid">
          {items.map((ev) => (
            <div key={ev.id} className="relative">
              <EvidenceThumb ev={ev} onOpen={setViewer} />
              {canDelete && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(ev); }}
                  className="absolute right-1 top-1 rounded-full bg-red-600 p-1 text-white opacity-90 shadow hover:bg-red-700"
                  aria-label="Eliminar"
                  data-testid={`evidence-delete-${ev.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-slate-500">
        Las imagenes se redimensionan automaticamente a {RESIZE_MAX_WIDTH}px de ancho para subida rapida. Maximo {MAX_IMAGE_MB} MB por imagen / {MAX_VIDEO_MB} MB por video. Solo el coordinador puede eliminar.
      </p>

      {viewer && <ViewerModal ev={viewer} onClose={() => setViewer(null)} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar evidencia</AlertDialogTitle>
            <AlertDialogDescription>
              Se ocultara del programa. La accion no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="evidence-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="evidence-delete-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
